<template>
    <div :class="['pm-carousel', { disabled: !hasMultipleResponses }]">
        <Carousel
            :value="responses"
            :num-visible="1"
            circular
            :show-indicators="hasMultipleResponses"
            :show-navigators="showCarouselIndicators && hasMultipleResponses"
            @update:page="praiseChanged"
        >
            <template #item="slotProps">
                <div
                    class="pm-carousel__content-container"
                    role="button"
                    @click="viewFullResponse(slotProps.data.id)"
                >
                    <div class="pm-carousel__score">
                        {{ slotProps.data.score }}
                    </div>

                    <div class="pm-carousel__details">
                        <div class="pm-carousel__details__heading">
                            <div class="pm-carousel__details__heading__name">
                                {{ slotProps.data.author }}
                            </div>
                            <div class="pm-carousel__details__heading__time">
                                {{
                                    shortenedPastRelativeDate(
                                        slotProps.data.created
                                    )
                                }}
                            </div>
                        </div>

                        <TextClamp
                            v-if="getFullComment(slotProps.data)"
                            :is-html="true"
                            class="pm-carousel__details__response"
                            :text="getFullComment(slotProps.data)"
                            :max-lines="5"
                        />
                        <div
                            v-else
                            class="pm-carousel__details__response pm-carousel__details__response--no-comment"
                        >
                            No comment
                        </div>
                    </div>
                </div>
            </template>
        </Carousel>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import Carousel from 'primevue/carousel'
import TextClamp from '@/components/TextClamp.vue'
import { ActionResponse } from '@/entities/frontlineManagerDash'
import { shortenedPastRelativeDate } from '@/utils/time'
import { useRouter } from 'vue-router'

interface Props {
    responses: ActionResponse[]
    showCarouselIndicators: boolean
    isMobile: boolean
}
const props = withDefaults(defineProps<Props>(), {
    showCarouselIndicators: true,
    isMobile: false,
})

const router = useRouter()

const emit = defineEmits(['praiseChange'])
const hasMultipleResponses = computed(() => (props.responses?.length ?? 0) > 1)
const praiseChanged = (number) => {
    emit('praiseChange', props.responses[number])
}
const viewFullResponse = (id) => {
    if (!id) return

    if (!props.isMobile) {
        window.open(`/chat/view/${id}`, '_blank')
    }
}

const getFullComment = (response: ActionResponse) => {
    if (!response?.additionalComment) {
        return response.response
    }
    if (!response.response) {
        return response.additionalComment
    }
    return `${response.response}<br />${response.additionalComment}`
}
</script>

<style lang="less" scoped>
@import '@/styles/prime-vue/variables.less';
@import '@/styles/rain/variables.less';

.pm-carousel {
    background: var(--p-primary-100);
    border-radius: @radius-xl;
    border: 1px solid var(--p-primary-300);
    margin-bottom: @spacing-xl;
    padding: @spacing-2xl @spacing-lg;
    position: relative;

    &.disabled {
        padding: @spacing-2xl;
    }

    :deep(.p-carousel-item) {
        display: flex;
        gap: @spacing-lg;
        padding: 0 @spacing-xs;
        box-sizing: border-box;
    }

    :deep(.p-carousel-next-button),
    :deep(.p-carousel-prev-button) {
        box-sizing: border-box;
        width: 32px;
        height: 32px;
        padding: @spacing-md;
        margin: @spacing-xxs;
        position: absolute !important; // Override PrimeVue inline styling
        bottom: @spacing-xl;

        &:hover {
            background: var(--p-primary-300);
        }
    }

    :deep(.p-carousel-prev-button) {
        left: @spacing-xl;
    }
    :deep(.p-carousel-next-button) {
        right: @spacing-xl;
    }
    :deep(.p-carousel-indicator-button) {
        height: 4px;
        width: 16px;
        border-radius: 20px;
    }
    :deep(.p-carousel-indicator-list) {
        padding: 0;
        margin: @spacing-3xl auto @spacing-md;
    }

    &__content-container {
        display: flex;
        gap: @spacing-lg;
        box-sizing: border-box;
        cursor: pointer;
        flex: 1;
    }

    &__score {
        font-size: @fontSize-lg;
        text-align: center;
        color: var(--p-success-500);
        font-weight: @fontWeight-bold;
        display: flex;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        align-items: center;
        justify-content: center;
        border: 4px solid var(--p-success-500);
        border-radius: 999px;
    }

    &__details {
        width: 100%;

        &__heading {
            display: flex;
            justify-content: space-between;
            padding-bottom: @spacing-sm;

            &__name {
                font-size: @fontSize-base;
                font-weight: 600;
                color: var(--p-gray-900);
            }
            &__time {
                font-size: @fontSize-sm;
                color: var(--p-gray-600);
            }
        }

        &__response {
            font-size: @fontSize-base;
            line-height: @lineHeight-base;

            &--no-comment {
                font-style: italic;
            }
        }
    }
}

@media (min-width: @tabletBreakpoint) {
    .pm-carousel {
        margin-bottom: 26px;
        padding: @spacing-2xl;
    }
}

@media (min-width: @desktopBreakpoint) {
    :deep(.p-carousel-indicator-list) {
        width: calc(100% - 96px); // Leave space on sides for navigator buttons
    }
}
</style>
